/**
 * Payment options as the savings panel and the presentation show them: the inputs the installer types (percent
 * in the form, fractions in the API), the monthly payment next to the monthly savings, payback text, and the
 * geometry of the cash-flow chart. The money itself comes from the savings engine (`SavingsResult`); the loan formula
 * here only previews a payment while the engine answers. Pure (no React), unit tested with node:test.
 */
import type { S } from "../api/client";
import type { Translate } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";

export type PaymentType = S["FinancingProduct"];
export type PaymentOption = S["PaymentOption"];
export type PaymentResult = S["PaymentResult"];

export const PAYMENT_TYPES: PaymentType[] = ["cash", "loan", "lease", "ppa"];
/** Catalog keys of the payment types' names: `t(PAYMENT_LABEL_KEY[type])`. */
export const PAYMENT_LABEL_KEY: Record<PaymentType, MessageKey> = {
  cash: "editor.payments.type.cash",
  loan: "editor.payments.type.loan",
  lease: "editor.payments.type.lease",
  ppa: "editor.payments.type.ppa",
};

/** The fields each payment option takes (the rest come from the company's financial settings when left blank). */
export const PAYMENT_FIELDS: Record<PaymentType, (keyof PaymentOption)[]> = {
  cash: [],
  loan: ["apr", "term_years", "down_payment_pct"],
  lease: ["monthly_payment", "escalator", "term_years"],
  ppa: ["rate_per_kwh", "escalator", "term_years"],
};

/** Level monthly payment of a loan: P·i / (1 − (1 + i)^−n), i = APR / 12, n = 12 × years (no interest: P / n). */
export function loanMonthlyPayment(principal: number, apr: number, years: number): number {
  const n = Math.round(years * 12);
  if (principal <= 0 || n <= 0) return 0;
  const i = apr / 12;
  if (i === 0) return principal / n;
  return (principal * i) / (1 - Math.pow(1 + i, -n));
}

/** Percent typed in a form → the API's fraction (and back), rounded to avoid 6.9 → 0.06900000000000001. */
export const toFraction = (percent: number | null | undefined): number | null => (percent == null || Number.isNaN(percent) ? null : Math.round(percent * 1e4) / 1e6);
export const toPercent = (fraction: number | null | undefined): number | null => (fraction == null ? null : Math.round(fraction * 1e6) / 1e4);

/** The option as the API takes it: blank fields dropped (the engine fills them from the settings). */
export function cleanOption(option: PaymentOption): PaymentOption {
  const out: PaymentOption = { type: option.type };
  for (const key of PAYMENT_FIELDS[option.type]) {
    const value = option[key];
    if (value != null && !(typeof value === "number" && Number.isNaN(value))) (out as Record<string, unknown>)[key] = value;
  }
  if (option.label) out.label = option.label;
  return out;
}

export interface MonthlyLine {
  payment: number;
  savings: number;
  /** savings − payment: what the customer is better off each month in year 1. */
  net: number;
  upfront: number;
}

export function monthlyLine(result: Pick<PaymentResult, "monthly_payment" | "monthly_savings_year1" | "upfront">): MonthlyLine {
  const payment = Math.max(0, result.monthly_payment);
  const savings = result.monthly_savings_year1;
  return { payment, savings, net: Math.round((savings - payment) * 100) / 100, upfront: result.upfront };
}

/** "8.4 years", or that it does not pay back within the analysis (in the reader's language). */
export function paybackText(t: Translate, years: number | null | undefined, horizon: number): string {
  if (years == null) return t("editor.payments.payback.never", { years: horizon });
  if (years <= 0) return t("editor.payments.payback.immediately");
  return t("editor.payments.payback.years", { years: Math.round(years * 10) / 10 });
}

/** The first year the cumulative cash flow reaches zero, interpolated within the year (null: never). */
export function crossover(flows: Pick<S["SavingsCashFlowYear"], "year" | "cumulative">[]): number | null {
  for (let k = 0; k < flows.length; k++) {
    const f = flows[k]!;
    if (f.cumulative >= 0) {
      if (k === 0) return f.year;
      const prev = flows[k - 1]!;
      const step = f.cumulative - prev.cumulative;
      return step > 0 ? prev.year + (0 - prev.cumulative) / step : f.year;
    }
  }
  return null;
}

export interface Bar {
  year: number;
  x: number;
  y: number;
  w: number;
  h: number;
  positive: boolean;
}

export interface ChartGeometry {
  bars: Bar[];
  zeroY: number;
  min: number;
  max: number;
  /** x of the payback point, if any. */
  paybackX: number | null;
}

/** Bars of the cumulative cash flow (year 0 = the outlay) in a `width` × `height` box, zero line included. */
export function cashFlowChart(flows: Pick<S["SavingsCashFlowYear"], "year" | "cumulative">[], width: number, height: number, pad = 4): ChartGeometry {
  const values = flows.map((f) => f.cumulative);
  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const span = max - min || 1;
  const inner = height - 2 * pad;
  const y = (v: number) => pad + ((max - v) / span) * inner;
  const zeroY = y(0);
  const slot = flows.length ? width / flows.length : width;
  const w = Math.max(1, slot * 0.72);
  const bars = flows.map((f, k) => {
    const top = y(Math.max(0, f.cumulative));
    const bottom = y(Math.min(0, f.cumulative));
    return { year: f.year, x: k * slot + (slot - w) / 2, y: top, w, h: Math.max(0.5, bottom - top), positive: f.cumulative >= 0 };
  });
  const payback = crossover(flows);
  const first = flows[0]?.year ?? 0;
  return { bars, zeroY, min, max, paybackX: payback == null ? null : (payback - first + 0.5) * slot };
}
